import { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { Camera, Cpu, Radar } from 'lucide-react';

const components = [
  {
    label: 'LiDAR',
    title: 'LiDAR',
    description:
      'The top-mounted laser scanner gives the Scout depth awareness, helping it map nearby walls, objects, and route changes while it moves.',
    Icon: Radar,
  },
  {
    label: 'Vision',
    title: 'Camera',
    description:
      'The front camera provides live visual context for operators and feeds image data into onboard perception workflows.',
    Icon: Camera,
  },
  {
    label: 'Mobility',
    title: 'Raspberry Pi + Wheel System',
    description:
      'The Raspberry Pi coordinates sensor input, motor control, and telemetry while the four-wheel base carries the Scout through indoor patrol routes.',
    Icon: Cpu,
  },
];

export function ScoutPage() {
  const pinRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: pinRef,
    offset: ['start start', 'end end'],
  });

  return (
    <section
      id="scout"
      className="scroll-mt-24 bg-neutral-950 pt-20 text-neutral-100 md:pt-24"
    >
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-white">
          Platform
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
          The Scout
        </h1>
        <p className="mt-4 max-w-3xl text-white">
          A compact autonomous ground platform built around mobile sensing, local compute,
          and a rugged wheel base.
        </p>
      </div>

      <div ref={pinRef} className="relative mt-10 h-[430vh] md:mt-16">
        <div className="sticky top-0 flex h-screen w-full items-center overflow-hidden">
          <ScoutScrollScene progress={scrollYProgress} />
        </div>
      </div>
    </section>
  );
}

function ScoutScrollScene({ progress }: { progress: MotionValue<number> }) {
  // Phase map (with clean holds, no cross-fade overlap between sibling panels):
  //   0.00 – 0.10  intro hold      both rovers side-by-side
  //   0.10 – 0.18  intro out
  //   0.18 – 0.26  LiDAR in        rover2 image enters from the right
  //   0.26 – 0.40  LiDAR HOLD
  //   0.40 – 0.46  LiDAR out
  //   0.46 – 0.54  Camera in       rover2 image stays
  //   0.54 – 0.68  Camera HOLD
  //   0.68 – 0.74  Camera out      rover2 image exits
  //   0.74 – 0.82  Drive in        rover1 image enters
  //   0.82 – 1.00  Drive HOLD
  const introOpacity = useTransform(progress, [0, 0.10, 0.18], [1, 1, 0]);
  const featureImageOpacity = useTransform(
    progress,
    [0.18, 0.26, 0.68, 0.74],
    [0, 1, 1, 0]
  );
  const featureImageX = useTransform(
    progress,
    [0.18, 0.26, 0.68, 0.74],
    [80, 0, 0, 80]
  );
  const driveImageOpacity = useTransform(progress, [0.74, 0.82, 1], [0, 1, 1]);
  const driveImageX = useTransform(progress, [0.74, 0.82], [80, 0]);
  const lidarOpacity = useTransform(progress, [0.18, 0.26, 0.40, 0.46], [0, 1, 1, 0]);
  const lidarY = useTransform(progress, [0.18, 0.26, 0.40, 0.46], [32, 0, 0, -32]);
  const cameraOpacity = useTransform(progress, [0.46, 0.54, 0.68, 0.74], [0, 1, 1, 0]);
  const cameraY = useTransform(progress, [0.46, 0.54, 0.68, 0.74], [32, 0, 0, -32]);
  const driveOpacity = useTransform(progress, [0.74, 0.82, 1], [0, 1, 1]);
  const driveY = useTransform(progress, [0.74, 0.82], [32, 0]);

  return (
    <div className="relative mx-auto h-full w-full max-w-7xl px-4 md:px-8">
      <motion.div
        style={{ opacity: introOpacity }}
        className="absolute inset-0 flex flex-col items-center justify-center gap-5 pt-20 md:flex-row md:gap-8 md:pt-24"
      >
        <img
          src="/rover1.png"
          alt="Front view of the Valinor Dynamics Scout rover"
          className="w-full max-w-[34rem] object-contain md:w-1/2"
        />
        <img
          src="/rover2.png"
          alt="Top view of the Valinor Dynamics Scout rover"
          className="hidden w-full max-w-[34rem] object-contain md:block md:w-1/2"
        />
      </motion.div>

      <motion.div
        style={{ opacity: introOpacity }}
        className="pointer-events-none absolute inset-x-4 bottom-10 text-center md:bottom-14"
      >
        <p className="text-xs uppercase tracking-[0.22em] text-white">
          Anatomy
        </p>
        <p className="mt-2 text-sm text-white">
          Scroll to explore the systems inside the Scout.
        </p>
      </motion.div>

      <div className="absolute bottom-10 right-4 w-[78%] max-w-2xl md:bottom-auto md:right-8 md:top-1/2 md:w-[52%] md:-translate-y-1/2">
        <motion.div style={{ opacity: featureImageOpacity, x: featureImageX }}>
          <img
            src="/rover2.png"
            alt="Scout rover showing top-mounted LiDAR and front camera"
            className="w-full object-contain"
          />
        </motion.div>
      </div>

      <div className="absolute bottom-8 right-4 w-[84%] max-w-2xl md:bottom-auto md:right-8 md:top-1/2 md:w-[52%] md:-translate-y-1/2">
        <motion.div style={{ opacity: driveImageOpacity, x: driveImageX }}>
          <img
            src="/rover1.png"
            alt="Scout rover showing Raspberry Pi, wiring, battery pack, and wheel base"
            className="w-full object-contain"
          />
        </motion.div>
      </div>

      <ComponentPanel component={components[0]} opacity={lidarOpacity} y={lidarY} />
      <ComponentPanel component={components[1]} opacity={cameraOpacity} y={cameraY} />
      <ComponentPanel component={components[2]} opacity={driveOpacity} y={driveY} />
    </div>
  );
}

interface ComponentPanelProps {
  component: (typeof components)[number];
  opacity: MotionValue<number>;
  y: MotionValue<number>;
}

function ComponentPanel({ component, opacity, y }: ComponentPanelProps) {
  const { Icon } = component;

  return (
    <div className="absolute left-4 top-20 z-10 w-[calc(100%-2rem)] max-w-xl md:left-8 md:top-1/2 md:w-[42%] md:-translate-y-1/2">
      <motion.div style={{ opacity, y }}>
        <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-neutral-500">
          <Icon className="h-5 w-5 text-white" />
          {component.label}
        </div>
        <h3 className="text-3xl font-semibold tracking-tight md:text-5xl">
          {component.title}
        </h3>
        <p className="mt-5 border-l border-white/25 pl-5 text-base leading-7 text-white md:text-lg md:leading-8">
          {component.description}
        </p>
      </motion.div>
    </div>
  );
}
